/**
 * Inquiry Service
 *
 * API calls for inquiry management
 * Tasks: T5.1.1 - T5.1.8, T5.2.1 - T5.2.6
 */

import { api } from '../lib/api';
import apiClient from '../lib/api';

// Types
export interface InquiryItem {
  productId: string;
  quantity: number;
  color?: string;
  notes?: string;
}

export interface CreateInquiryData {
  type: 'SAMPLE_REQUEST' | 'BULK_ORDER';
  items: InquiryItem[];
  deliveryAddress: string;
  logoDescription?: string;
  timeline?: string;
  notes?: string;
}

export interface InquiryListItem {
  id: string;
  referenceNo: string;
  type: 'SAMPLE_REQUEST' | 'BULK_ORDER';
  status: InquiryStatus;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
  hasQuote: boolean;
}

export type InquiryStatus =
  | 'PENDING'
  | 'UNDER_REVIEW'
  | 'QUOTE_SENT'
  | 'QUOTE_ACCEPTED'
  | 'QUOTE_DECLINED'
  | 'QUOTE_REVISION_REQUESTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED';

export interface InquiryDetail {
  id: string;
  referenceNo: string;
  type: 'SAMPLE_REQUEST' | 'BULK_ORDER';
  status: InquiryStatus;
  deliveryAddress: string | null;
  logoDescription: string | null;
  timeline: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string | null;
    email: string;
    companyName: string | null;
  };
  items: Array<{
    id: string;
    quantity: number;
    color: string | null;
    notes: string | null;
    product: {
      id: string;
      name: string;
      slug: string;
      sku: string;
      primaryImage: string | null;
    };
  }>;
  quote: Quote | null;
  messages: Message[];
}

export interface Quote {
  id: string;
  totalAmount: number;
  validityDate: string;
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';
  pdfUrl: string | null;
  items: Array<{
    id: string;
    quantity: number;
    unitPrice: number;
    total: number;
    product: {
      id: string;
      name: string;
      sku: string;
    };
  }>;
}

export interface Message {
  id: string;
  senderType: 'USER' | 'ADMIN';
  message: string;
  createdAt: string;
}

export interface ListInquiriesParams {
  page?: number;
  limit?: number;
  type?: 'SAMPLE_REQUEST' | 'BULK_ORDER';
  status?: InquiryStatus;
  startDate?: string;
  endDate?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

// Submit inquiry with inline OTP
export interface SubmitInquiryWithOTPData {
  phone: string;
  otp: string;
  name: string;
  companyName?: string;
  city: string;
  items: InquiryItem[];
  type: 'SAMPLE_REQUEST' | 'BULK_ORDER';
  notes?: string;
}

export interface SubmitInquiryResponse {
  inquiry: InquiryDetail;
  user: {
    id: string;
    email: string | null;
    phone: string | null;
    name: string | null;
    companyName: string | null;
    designation: string | null;
    city: string | null;
    authMethod: string;
    profilePicture: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
  isNewUser: boolean;
}

/**
 * Submit inquiry with inline OTP verification (public endpoint)
 */
export async function submitInquiryWithOTP(data: SubmitInquiryWithOTPData): Promise<SubmitInquiryResponse> {
  const response = await api.post<SubmitInquiryResponse>(
    '/inquiries/submit',
    data
  ) as unknown as { success: boolean; data: SubmitInquiryResponse };
  return response.data;
}

/**
 * Create a new inquiry
 */
export async function createInquiry(data: CreateInquiryData): Promise<InquiryDetail> {
  const response = await api.post<InquiryDetail>(
    '/inquiries',
    data
  ) as unknown as { success: boolean; data: InquiryDetail };
  return response.data;
}

/**
 * List user's inquiries
 */
export async function listInquiries(
  params: ListInquiriesParams = {}
): Promise<PaginatedResponse<InquiryListItem>> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.type) searchParams.set('type', params.type);
  if (params.status) searchParams.set('status', params.status);
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);

  // api.get already unwraps to { success, data, meta }
  // We need to return { data, meta } for PaginatedResponse
  const response = await api.get<InquiryListItem[]>(
    `/inquiries?${searchParams.toString()}`
  ) as unknown as { success: boolean; data: InquiryListItem[]; meta: PaginatedResponse<InquiryListItem>['meta'] };

  return {
    data: response.data ?? [],
    meta: response.meta ?? { page: 1, limit: 10, total: 0, hasMore: false },
  };
}

/**
 * Get inquiry by ID
 */
export async function getInquiry(id: string): Promise<InquiryDetail> {
  // api.get already unwraps to { success, data }
  const response = await api.get<InquiryDetail>(
    `/inquiries/${id}`
  ) as unknown as { success: boolean; data: InquiryDetail };
  return response.data;
}

/**
 * Add message to inquiry
 */
export async function addMessage(
  inquiryId: string,
  message: string
): Promise<{ id: string; message: string; createdAt: string }> {
  const response = await api.post<{ id: string; message: string; createdAt: string }>(
    `/inquiries/${inquiryId}/messages`,
    { message }
  ) as unknown as { success: boolean; data: { id: string; message: string; createdAt: string } };
  return response.data;
}

/**
 * Get quote for inquiry
 */
export async function getQuote(inquiryId: string): Promise<Quote | null> {
  const response = await api.get<Quote | null>(
    `/inquiries/${inquiryId}/quote`
  ) as unknown as { success: boolean; data: Quote | null };
  return response.data;
}

/**
 * Accept quote
 */
export async function acceptQuote(inquiryId: string): Promise<InquiryDetail> {
  const response = await api.post<InquiryDetail>(
    `/inquiries/${inquiryId}/quote/accept`
  ) as unknown as { success: boolean; data: InquiryDetail };
  return response.data;
}

/**
 * Decline quote
 */
export async function declineQuote(
  inquiryId: string,
  reason?: string
): Promise<InquiryDetail> {
  const response = await api.post<InquiryDetail>(
    `/inquiries/${inquiryId}/quote/decline`,
    { reason }
  ) as unknown as { success: boolean; data: InquiryDetail };
  return response.data;
}

/**
 * Request quote revision
 */
export async function requestRevision(
  inquiryId: string,
  reason?: string
): Promise<InquiryDetail> {
  const response = await api.post<InquiryDetail>(
    `/inquiries/${inquiryId}/quote/revision`,
    { reason }
  ) as unknown as { success: boolean; data: InquiryDetail };
  return response.data;
}

// Catalogue APIs
export interface CatalogueItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  bannerUrl: string | null;
  productCount: number;
  catalogueUrl: string | null;
  catalogueGeneratedAt: string | null;
}

/**
 * List available catalogues
 */
export async function listCatalogues(): Promise<CatalogueItem[]> {
  const response = await api.get<CatalogueItem[]>(
    '/catalogues'
  ) as unknown as { success: boolean; data: CatalogueItem[] };
  return response.data ?? [];
}

/**
 * Download catalogue PDF
 * Returns a blob URL for the downloaded PDF
 */
export async function downloadCatalogue(
  categoryId: string
): Promise<{ downloadUrl: string; fileName: string }> {
  // Use apiClient directly with blob response type (includes auth token via interceptor)
  const response = await apiClient.get(`/catalogues/${categoryId}/download`, {
    responseType: 'blob',
    timeout: 120000, // 2 minutes timeout for PDF generation
  });

  // Get filename from Content-Disposition header
  const contentDisposition = response.headers['content-disposition'];
  let fileName = 'Catalogue.pdf';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) {
      fileName = match[1];
    }
  }

  // Create blob URL
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const downloadUrl = URL.createObjectURL(blob);

  return { downloadUrl, fileName };
}
